import logger from "../logger";
import { ApiError } from "../types";

const generateSpanID = () => {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString();
};

export type ApiFetcherOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
  baseUrl: string;
  name: string;
  next?: {
    tags?: string[];
  };
};

const isAbortError = (
  error: unknown,
): error is Error & { name: "AbortError" } => {
  return error instanceof Error && error.name === "AbortError";
};

export default async function apiFetch<
  TData,
  TError,
  TBody extends {} | FormData | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
  baseUrl,
  name,
  next,
}: ApiFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  try {
    const requestHeaders: HeadersInit = {
      "Content-Type": "application/json",
      ...headers,
      "x-datadog-trace-id": generateSpanID(),
      "x-datadog-parent-id": generateSpanID(),
      "x-datadog-sampling-priority": "1",
    };

    /**
     * As the fetch API is being used, when multipart/form-data is specified
     * the Content-Type header must be deleted so that the browser can set
     * the correct boundary.
     * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
     */
    if (
      requestHeaders["Content-Type"] &&
      requestHeaders["Content-Type"]
        .toLowerCase()
        .includes("multipart/form-data")
    ) {
      delete requestHeaders["Content-Type"];
    }

    const path = resolveUrl(url, queryParams, pathParams);
    const response = await fetch(`${baseUrl}${path}`, {
      signal,
      method: method.toUpperCase(),
      body: body
        ? body instanceof FormData
          ? body
          : JSON.stringify(body)
        : undefined,
      headers: requestHeaders,
      next,
    } as RequestInit);

    if (!response.ok) {
      const error = new Error(
        `Failed to fetch from ${name} api ${path} with status ${response.status}`,
      ) as Error & { cause: ApiError | Response } & {
        cause: { status: number };
      };

      if (response.headers.get("content-type")?.includes("json")) {
        let cause: ApiError;
        try {
          cause = await response.json();
          error.cause = {
            ...cause,
            status: response.status,
          };
        } catch (error) {
          logger.error("Failed to parse json", error);
        }
      } else {
        error.cause = response;
      }

      throw error;
    }

    if (response.status === 404) {
      const error = new Error(`Not found on ${name} api`);
      error.cause = response;
      throw error;
    }

    if (response.headers.get("content-type")?.includes("json")) {
      return await response.json();
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      const blob = (await response.blob()) as unknown as TData;
      if (blob instanceof Blob && blob.size === 0) {
        return undefined as TData;
      }
      return blob;
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }

    logger.error("Failed to fetch!", error, {
      path: resolveUrl(url, queryParams, pathParams),
    });
    throw error;
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string> = {},
  pathParams: Record<string, string> = {},
) => {
  let query = new URLSearchParams(queryParams).toString();
  if (query) query = `?${query}`;
  return (
    url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)] || "") + query
  );
};
